// Stacked card layout for a single task. Used on viewports below the `md`
// breakpoint, where the 5-column `TaskLine` table doesn't fit and column
// widths fight with variable-length content (e.g. "Soon" vs "Not Due"
// status pills).
//
// The card mirrors `TaskLine`'s click + keyboard navigation so behavior is
// identical across both layouts; only the visual presentation differs.
// `readOnly` is TaskLine's too: plain data, no role, no focus stop, no click.
//
// Photos sit at the foot of the card body -- there is no continuation row to
// hang them from here, and the card is already a vertical stack. The role rule
// is TaskLine's, widened the same way: a card holding the strip is no longer a
// button. `tabIndex` goes with it, not just `role`, because a <button> inside a
// `role="button" tabIndex=0` div is the same nested-interactive trap.

import { useNavigate } from 'react-router-dom';
import { StatusBadge } from '../../components';
import { EntryPhotoStrip } from '../logbook/EntryPhotoStrip';
import { shortDisplayDate } from '../../fleet-shared/Common.mjs';
import {
  AssigneeCell,
  assigneeLinkUrl,
  displayPriorityForTaskLine,
  formatCounterInterval,
  formatInterval,
  navigateToTask,
} from './taskRowHelpers';

// One labelled detail row inside the card body. Renders nothing when
// `value` is falsy so cards stay compact for tasks with sparse fields.
const DetailRow = ({ label, value, secondary }) => {
  if (!value && value !== 0) {
    return null;
  }
  return (
    <div className="small">
      <div>
        <span className="text-body-secondary">{label}:</span>{' '}
        <span>{value}</span>
      </div>
      {secondary ? (
        <div className="text-body-secondary">{secondary}</div>
      ) : null}
    </div>
  );
};

export const TaskCard = ({
  task,
  onTaskClicked,
  tasksListBucket,
  assigneeLinkFor,
  readOnly = false,
  photos,
  onOpenPhoto,
  photoLimit,
  rowActions,
}) => {
  const navigate = useNavigate();

  // Same answer as TaskLine: a card holding the assignee link is no longer a
  // button (no nested interactive content) but still opens on click. The link
  // stops its own keydown, so the card's Enter/Space handler never sees it.
  const assigneeUrl = assigneeLinkUrl(task, assigneeLinkFor);
  const withPhotos = Boolean(photos && photos.length > 0 && onOpenPhoto);
  // TaskLine's rule: either a strip or an explicit control means the card holds
  // a <button> and can no longer be one itself.
  const withControls = Boolean(assigneeUrl) || withPhotos || Boolean(rowActions);

  function taskClicked() {
    if (readOnly) {
      return;
    }
    if (onTaskClicked) {
      onTaskClicked(task);
      return;
    }
    navigateToTask(navigate, task, tasksListBucket);
  }

  function onKeyDown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      taskClicked();
    }
  }

  const counterValue = task.dueCounter
    ? `${task.dueCounter}${task.itemCounter ? ` ${task.itemCounter.name}` : ''}`
    : '';
  // `shortDisplayDate(null)` returns '-' (a truthy placeholder) so we must
  // null-check `dueDate` here; otherwise `DetailRow` keeps the row and the
  // card shows a "Due (date) -" line for counter-only tasks.
  const dateValue = task.dueDate ? shortDisplayDate(task.dueDate) : '';
  const priority = displayPriorityForTaskLine(task.priority);

  return (
    <div
      className="card task-card"
      role={withControls || readOnly ? undefined : 'button'}
      tabIndex={withControls || readOnly ? undefined : 0}
      onClick={readOnly ? undefined : taskClicked}
      onKeyDown={readOnly ? undefined : onKeyDown}
      style={readOnly ? undefined : { cursor: 'pointer' }}
    >
      <div className="card-body p-3">
        <div className="d-flex align-items-start gap-2 mb-2">
          <StatusBadge status={task.bucketName} />
          <div className="flex-grow-1 min-w-0">
            <div className="entered-data fw-semibold text-break">{task.name}</div>
            {task.description ? (
              <div className="text-body-secondary small text-break">{task.description}</div>
            ) : null}
            {task.notes ? (
              <div className="text-body-secondary small text-break">{task.notes}</div>
            ) : null}
          </div>
        </div>

        <DetailRow
          label="Due (counter)"
          value={counterValue}
          secondary={formatCounterInterval(task)}
        />
        <DetailRow
          label="Due (date)"
          value={dateValue}
          secondary={formatInterval(task)}
        />
        <DetailRow
          label="Assigned"
          value={task.assignedEmail ? <AssigneeCell task={task} url={assigneeUrl} /> : ''}
        />
        <DetailRow label="Priority" value={priority} />

        {withPhotos && (
          <div className="mt-2">
            <EntryPhotoStrip
              photos={photos}
              onOpenPhoto={onOpenPhoto}
              onOpenEntry={taskClicked}
              limit={photoLimit}
            />
          </div>
        )}

        {rowActions && <div className="mt-2">{rowActions}</div>}
      </div>
    </div>
  );
};
